// Pure helpers for the /start and /ios redirect pages.
//
// Neither Vue's `useRoute().query` nor `window.location.search` can be trusted on mount, because
// both pages are prerendered:
// - 2026-09-18: when the redirect fires immediately, the router has not necessarily populated
//   `query` yet, and the destination silently lost every campaign parameter.
// - 2026-10-03: while hydrating `/start/?…`, the router briefly replaces the URL with the bare
//   path (`/start`, no query) and then restores it; a mount hook that runs in that gap reads an
//   empty `location.search`. In production that dropped every campaign param from /start and
//   every `ct` from /ios. Only the prerendered build shows it — the dev server serves the route
//   without the trailing slash and never rewrites the URL.
// `loadedSearch` reads the query from the navigation timing entry, which keeps the URL the
// document was actually loaded from.

/**
 * The query string the page was loaded with: from the navigation timing entry's URL when it
 * has one, else the current `location.search`. Pass
 * `performance.getEntriesByType('navigation')[0]?.name` and `window.location.search`.
 */
export function loadedSearch(navigationUrl: string | undefined, currentSearch: string): string {
  if (navigationUrl) {
    try {
      const { search } = new URL(navigationUrl);
      if (search) return search;
    } catch {
      // Not a URL; fall back to the live location.
    }
  }
  return currentSearch;
}

/** Params the app's `signupAcquisitionPayload` turns into an attribution touch. */
const FORWARDED_NAMES = new Set(['fbclid', 'gclid', 'gbraid', 'wbraid', 'w369_src', 'w369_plc']);
const FORWARDED_PREFIX = 'utm_';

/** The app bounds these the same way; an over-long click id is corrupt, so it is dropped whole. */
const MAX_PARAMS = 20;
const MAX_VALUE_LENGTH = 500;

const isCampaignParam = (name: string) => FORWARDED_NAMES.has(name) || name.startsWith(FORWARDED_PREFIX);

/**
 * `destination` with the campaign params from `search` copied onto it. Anything that is not a
 * campaign param is left behind: this page is a hop, not an open redirect.
 */
export function forwardCampaignParams(search: string, destination: string): string {
  const url = new URL(destination);
  const incoming = new URLSearchParams(search.replace(/^\?/, ''));

  let forwarded = 0;
  for (const [name, value] of incoming) {
    if (forwarded >= MAX_PARAMS) break;
    if (!isCampaignParam(name)) continue;
    if (value === '' || value.length > MAX_VALUE_LENGTH) continue;
    url.searchParams.set(name, value);
    forwarded += 1;
  }

  return url.toString();
}

/** The Meta click id on this visit, or '' when there is none. */
export function clickIdFrom(search: string): string {
  const value = new URLSearchParams(search.replace(/^\?/, '')).get('fbclid');
  return value && value.length <= MAX_VALUE_LENGTH ? value : '';
}

/**
 * True once the Pixel has recorded this visit, which is the only reason these pages pause at all.
 *
 * With a click id, that means `_fbc` carries THIS id — not merely that an `_fbc` exists, which a
 * returning visitor has from an older click. Without one, an `_fbp` is all there is to wait for.
 */
export function hasRecordedClick(cookieHeader: string, clickId: string): boolean {
  const value = (name: string): string => {
    for (const part of cookieHeader.split(';')) {
      const eq = part.indexOf('=');
      if (eq <= 0) continue;
      if (part.slice(0, eq).trim() !== name) continue;
      return part.slice(eq + 1).trim();
    }
    return '';
  };

  if (clickId) {
    // fbevents writes `fb.<subdomain>.<timestamp>.<fbclid>`, so the id is the last segment.
    const fbc = value('_fbc');
    return fbc !== '' && fbc.slice(fbc.lastIndexOf('.') + 1) === clickId;
  }

  return value('_fbp') !== '';
}

/** Workspace369 on the App Store, and the provider token App Store Connect credits installs to. */
export const APP_STORE_ID = '6475013419';
export const APP_STORE_PROVIDER_TOKEN = '126817027';

/** Apple accepts letters, digits, dash, underscore and dot in `ct`, up to 30 characters. */
const CAMPAIGN_TOKEN_MAX = 30;
/** Fallback `ct` for a Meta ad that sets no token of its own: this prefix plus its `utm_content`. */
const META_TOKEN_PREFIX = 'w369_meta_';

const cleanToken = (raw: string) => raw.replace(/[^A-Za-z0-9._-]/g, '');

/**
 * The App Store campaign token for this visit: the ad's own `ct` when it has one, otherwise
 * `w369_meta_<utm_content>` so every Meta ad still lands in its own App Store Connect campaign.
 * '' when there is neither. A `ct` with no characters Apple accepts counts as missing.
 */
export function campaignTokenFrom(search: string): string {
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const ct = cleanToken(params.get('ct') ?? '');
  if (ct) return ct.slice(0, CAMPAIGN_TOKEN_MAX);
  const content = cleanToken(params.get('utm_content') ?? '');
  return content ? `${META_TOKEN_PREFIX}${content}`.slice(0, CAMPAIGN_TOKEN_MAX) : '';
}

/**
 * The optional App Store listing, with the campaign token (`ct`), the provider token
 * (`pt`, without which App Store Connect cannot attribute the install to a campaign) and `mt=8`.
 */
export function appStoreUrl(search: string): string {
  const url = new URL(`https://apps.apple.com/app/id${APP_STORE_ID}`);
  const ct = campaignTokenFrom(search);
  if (ct) url.searchParams.set('ct', ct);
  url.searchParams.set('pt', APP_STORE_PROVIDER_TOKEN);
  url.searchParams.set('mt', '8');
  return url.toString();
}
